import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LucideAngularModule, Search, Database, FileText, Check, RefreshCw, TriangleAlert } from 'lucide-angular';
import { KnowledgeBaseService } from '../../core/services/knowledge-base.service';
import { KnowledgeBase } from '../../core/models/knowledge-base.model';
import { RecentItemsService } from '../../core/services/recent-items.service';

/**
 * Wrapper component for knowledge base selection
 * Shows knowledge base grid for user to choose from
 */
@Component({
  selector: 'app-knowledge-base-select-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    LucideAngularModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="knowledge-base-container">
      <div class="header-section">
        <h1 class="titulo">Seleccionar Base de Conocimiento</h1>
        <p class="normal">Elige una base de conocimiento para trabajar</p>
      </div>
    
      <div class="flex-1 max-w-md mb-8">
        <div class="relative">
          <input
            [value]="searchQuery"
            (input)="onSearchChange($any($event.target).value)"
            placeholder="Buscar base de conocimiento..."
            type="text"
            class="search-input pl-12"
            />
          <lucide-angular
            class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary"
            [img]="Search">
          </lucide-angular>
        </div>
      </div>
    
      @if (loading()) {
        <!-- Loading Spinner -->
        <div class="flex flex-col items-center justify-center py-16">
          <mat-spinner diameter="50"></mat-spinner>
          <p class="pt-4 normal">
            Cargando bases de conocimiento...
          </p>
        </div>
      } @else if (error()) {
        <div class="error-state">
          <p>{{ error() }}</p>
        </div>
      } @else {
        <!-- Knowledge Base Cards Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          @for (knowledgeBase of filteredKnowledgeBases; track knowledgeBase.knowledgeBaseId) {
            <div
              (click)="selectKnowledgeBase(knowledgeBase.knowledgeBaseId)"
              class="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-6 hover:shadow-medium hover:border-light-primary/30 dark:hover:border-dark-primary/30 transition-all duration-200 cursor-pointer group"
              >
              <!-- KB Avatar -->
              <div class="flex items-center justify-center mb-4">
                <div
                  class="w-16 h-16 rounded-2xl flex items-center justify-center shadow-soft group-hover:shadow-medium transition-all duration-200 relative overflow-hidden bg-gradient-to-br from-light-primary to-light-primary-variant dark:from-dark-primary dark:to-dark-primary-variant"
                  >
                  <div class="absolute inset-0 bg-black/10 mix-blend-overlay"></div>
                  <lucide-angular
                    [img]="Database"
                    class="w-8 h-8 text-white relative z-10"
                  ></lucide-angular>
                </div>
              </div>
    
              <!-- KB Info -->
              <div class="text-center mb-4">
                <h3 class="font-bold text-lg text-light-text dark:text-dark-text !mb-1">
                  {{ knowledgeBase.name }}
                </h3>
                <p class="normal min-h-[30px]">
                  @if (knowledgeBase.qaSynchronizationStatus === 'SYNCHRONIZING' || knowledgeBase.prodSynchronizationStatus === 'SYNCHRONIZING') {
                    PROCESO_EN_CURSO
                  } @else if (knowledgeBase.qaSynchronizationStatus !== 'SYNCHRONIZING' && knowledgeBase.prodSynchronizationStatus !== 'SYNCHRONIZING') {
                    PROCESO_FINALIZADO
                  }
                </p>
                <p class="normal text-text-secondary !mb-1">Estado de Sincronización</p>
                <div class="flex flex-row items-center justify-center gap-2">
                  @if (getKnowledgeBaseConfig(knowledgeBase.qaSynchronizationStatus); as config) {
                    <div class="flex items-center justify-center gap-1 px-3 py-2 rounded-xl" [ngClass]="config.color">
                      <lucide-angular [img]="config.icon" [class]="config.iconClass" class="w-4 h-4"></lucide-angular>
                      <span class="text-xs">QA</span>
                    </div>
                  }
                  @if (getKnowledgeBaseConfig(knowledgeBase.prodSynchronizationStatus); as config) {
                    <div class="flex items-center justify-center gap-1 px-3 py-2 rounded-xl" [ngClass]="config.color">
                      <lucide-angular [img]="config.icon" class="w-4 h-4"></lucide-angular>
                      <span class="text-xs">PROD</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
    
        <!-- Empty State -->
        @if (filteredKnowledgeBases.length === 0) {
          <div class="text-center py-16">
            <div class="w-20 h-20 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-3xl flex items-center justify-center mx-auto mb-4">
              <lucide-angular
                [img]="Database"
                class="w-10 h-10 text-light-text-tertiary dark:text-dark-text-tertiary"
              ></lucide-angular>
            </div>
            <h3 class="text-xl font-semibold text-light-text dark:text-dark-text mb-2">
              No se encontró ninguna base de conocimiento
            </h3>
            <p class="normal">
              Intenta ajustar tus términos de búsqueda
            </p>
          </div>
        }
      }
    </div>
    `,
  styles: [`
    .knowledge-base-container {
      padding: 2rem;
      margin: 0 auto;
    }

    .header-section {
      margin-bottom: 2rem;
    }

    .loading-state,
    .error-state {
      text-align: center;
      padding: 3rem;
      color: var(--text-secondary);
    }
  `]
})
export class KnowledgeBaseSelectWrapperComponent implements OnInit {
  readonly Search = Search;
  readonly Database = Database;
  readonly FileText = FileText;
  readonly Check = Check;
  readonly RefreshCw = RefreshCw;
  readonly TriangleAlert = TriangleAlert;

  searchQuery = '';
  knowledgeBases = signal<KnowledgeBase[]>([]);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  constructor(
    private readonly router: Router,
    private readonly knowledgeBaseService: KnowledgeBaseService,
    private readonly recentItemsService: RecentItemsService
  ) {}

  ngOnInit(): void {
    this.loadKnowledgeBases();
  }

  loadKnowledgeBases(): void {
    this.loading.set(true);
    this.error.set(null);

    this.knowledgeBaseService.getKnowledgeBasesByUser().subscribe({
      next: (knowledgeBases) => {
        this.knowledgeBases.set(knowledgeBases);
        this.loading.set(false);
      },
      error: (error) => {
        this.error.set('Error al cargar las bases de conocimiento');
        console.error('KnowledgeBaseSelectWrapperComponent: Error loading knowledge bases', error);
        this.loading.set(false);
      },
    });
  }

  get filteredKnowledgeBases(): KnowledgeBase[] {
    if (!this.searchQuery.trim()) {
      return this.knowledgeBases();
    }

    const query = this.searchQuery.toLowerCase();
    return this.knowledgeBases().filter(
      (kb) => kb.name.toLowerCase().includes(query)
    );
  }

  selectKnowledgeBase(knowledgeBaseId: number): void {    
    const kb = this.knowledgeBases().find((k) => k.knowledgeBaseId === knowledgeBaseId);
    if (kb) {
      this.recentItemsService.addRecentKnowledgeBase(kb.code, kb.name, ['/knowledge-base', kb.code]);
      this.router.navigate(['/knowledge-base', kb.code], {
        state: { knowledgeBase: kb }
      });
    }
  }

  /**
   * Handle search input changes
   */
  onSearchChange(value: string): void {
    this.searchQuery = value;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Static configuration for knowledge base status icons and colors
   */
  private readonly KNOWLEDGE_BASE_STATUS_MAP: Record<string, {icon: any; iconClass: string; color: string}> = {
    'SYNCHRONIZING': { 
      icon: RefreshCw, 
      iconClass: 'animate-spin',
      color: 'border border-gray-300 dark:border-gray-500 bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-100' 
    },
    'ERROR': { 
      icon: TriangleAlert, 
      iconClass: '',
      color: 'border border-amber-300 dark:border-amber-500 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-100' 
    },
    'null': { 
      icon: Check, 
      iconClass: '',
      color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-900' 
    }
  };

  /**
   * Get icon and color configuration based on knowledge base synchronization status
   */
  getKnowledgeBaseConfig(synchronizationStatus: string | null): { icon: any; iconClass: string; color: string } {
    if (synchronizationStatus === 'SYNCHRONIZING') {
      return this.KNOWLEDGE_BASE_STATUS_MAP['SYNCHRONIZING'];
    } else if (synchronizationStatus === 'ERROR') {
      return this.KNOWLEDGE_BASE_STATUS_MAP['ERROR'];
    } else { //null
      return this.KNOWLEDGE_BASE_STATUS_MAP['null'];
    }
  }
}
